import { useCallback, useEffect, useRef, useState } from "react";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { camelCase, startCase } from "lodash";
import {
  MODAL_WIDTH,
  MODAL_ARROW_HEIGHT,
  MODAL_HEIGHT_NO_TAG,
  MODAL_HEIGHT_WITH_TAG,
  MODAL_OFFSET_TOP_WITHOUT_TAG,
  ROW_HEIGHT,
  NAV_HEIGHT_PX,
  PNL_COLORS,
  ROW_PADDING,
  useBreakpoints,
  getIsCypressRunning,
  MODAL_PADDING,
  BREAKPOINT_DESKTOP_XL,
  Z_INDEX,
} from "../../../../constants/constants";
import useWindowSize from "utils/customHooks/useWindowSize";
import { TIME_AXIS_MARGIN_HZ } from "../../TimelinesStyles";
import {
  useTimeAxisContext,
  useGetPageFromPath,
} from "utils/customHooks/genericHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { TimelineTooltipStyles } from "./TimelineTooltipStyles";
import { animated, useSpring } from "@react-spring/web";
import TooltipTabs from "./TimelineTooltipTabs";
import { PnlCategoriesEnum } from "types/enums";
import { MoreInfoPanel } from "./TimelineTooltipPanels/MoreInfoPanel";
import { AboutPanel } from "./TimelineTooltipPanels/AboutPanel";
import {
  useHandleCloseModal,
  useTrackTimeAxisTop,
} from "./timelineTooltipUtils";
import { RemoveRetrotagForm } from "./RemoveRetrotagForm";
import { TimeRangeFinite } from "types/types";
import { useHighlightOverlappingSegments, useIsDarkMode } from "store/atoms";
import { useTimelineTooltipStore } from "./timelineTooltipStore";
import {
  useDataSourceGroups,
  useHighlightedTags,
} from "components/Dashboard/HubletView/SingleTimeline/singleTimelineStore";
import { useKey, useLocalStorage } from "react-use";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useIsTakingSnapshot } from "pages/SnapshotsPage/SnapshotElementButton";

enum TooltipPanel {
  About,
  MoreInfo,
}

type TimelineTooltipProps = {
  offsets: number[];
  isSingleTimeline: boolean;
};
const TimelineTooltip = ({
  offsets,
  isSingleTimeline,
}: TimelineTooltipProps) => {
  useCloseTooltipWhenScrollTooFar();

  const [{ modal }] = useTimelineTooltipStore();
  const { scaleTime, xRight } = useTimeAxisContext();

  // modify z-index of timeline while tooltip is open so it shows up on top of date picker
  useEffect(() => {
    const timeline = document.getElementById("timelineStyles");
    if (!timeline) {
      return;
    }
    if (modal) {
      timeline.classList.add("tooltipOpen");
    } else {
      timeline?.classList.remove("tooltipOpen");
    }
  }, [modal]);
  const { selectedDataSourceGroups } = useDataSourceGroups();
  const isViewingAlternateTimelines = selectedDataSourceGroups.length > 0;
  if (!modal) {
    return null;
  }
  const { clickIndex, t } = modal;
  const needsYOffsetAdjust = isViewingAlternateTimelines && clickIndex > 0;

  const y =
    offsets[clickIndex] +
    (2.5 +
      // when we're viewing alternate timelines, there are 2x as many rows in the same space, so divide y by 2
      (needsYOffsetAdjust ? -1 : 0)) *
      (ROW_HEIGHT + ROW_PADDING) +
    (needsYOffsetAdjust ? -ROW_HEIGHT / 4 : 0);
  const xIdeal = (scaleTime(t) || 0) - 0.5 * MODAL_WIDTH - MODAL_PADDING;
  const xActual = xIdeal < 0 ? 0 : Math.min(xIdeal, xRight - MODAL_WIDTH + 0);
  const deltaX = xActual - xIdeal;

  return (
    <TimelineTooltipContent
      top={y}
      left={xActual}
      deltaX={deltaX}
      isSingleTimeline={isSingleTimeline}
    />
  );
};

export default TimelineTooltip;

type TimelineTooltipContentProps = {
  top: number;
  left: number;
  deltaX: number;
  isSingleTimeline?: boolean;
};

// tslint:disable-next-line: cognitive-complexity
export function TimelineTooltipContent({
  deltaX,
  left,
  top,
  isSingleTimeline = false,
}: TimelineTooltipContentProps) {
  const [
    {
      general: {
        timelineTooltipShowCounts,
        timelineTooltipEnableSignalValidation,
      },
    },
  ] = useFlightConfig();
  const { isMobileOrLarger } = useBreakpoints();
  const [highlightedTags] = useHighlightedTags();
  const { selectedDataSourceGroups } = useDataSourceGroups();
  const areAnyTagsHighlighted =
    [...highlightedTags, ...selectedDataSourceGroups].length > 0;
  const [highlightOverlappingSegments] = useHighlightOverlappingSegments();
  const { isTimelinePage, isHubletPage, isTrendsPage, isGridLayoutPage } =
    useGetPageFromPath();
  const [isDarkMode] = useIsDarkMode();
  const [
    { selectedProdSegment, machemetryId, isModalPinned, selectedTimelineIdx },
  ] = useTimelineTooltipStore();
  const { innerWidth } = useWindowSize();

  const timeAxisTop = useTrackTimeAxisTop();
  const { translate } = useFlightTranslation();

  const [removeRetrotagFormOpen, setRemoveRetrotagFormOpen] = useState(false);

  const [tabIndex, setTabIndex] = useLocalStorage<TooltipPanel>(
    "timelineTooltip:tabIndex",
    TooltipPanel.About
  );
  const selectedSegmentRef = useRef(selectedProdSegment);
  // must compare segment ranges instead of segment objs because query refetches will change the segment obj
  const hasSelectedSegmentChanged =
    selectedSegmentRef.current?.range?.ge !== selectedProdSegment?.range?.ge &&
    selectedSegmentRef.current?.range?.lt !== selectedProdSegment?.range?.lt;
  if (hasSelectedSegmentChanged) {
    setRemoveRetrotagFormOpen(false);
    selectedSegmentRef.current = selectedProdSegment;
    if (tabIndex !== TooltipPanel.About && !timelineTooltipShowCounts) {
      setTabIndex(TooltipPanel.About);
    }
  }
  const openRemoveRetrotagForm = useCallback(
    () => setRemoveRetrotagFormOpen(true),
    [setRemoveRetrotagFormOpen]
  );

  const ref = useCloseTooltipOnMouseout();

  const [isTakingSnapshot] = useIsTakingSnapshot();
  if (!selectedProdSegment || isTakingSnapshot) {
    return null;
  }

  const { tag, pnl, range } = selectedProdSegment;

  if (!range?.ge) {
    return null;
  }

  const metadata = selectedProdSegment?.meta;
  const numTraceabilityComments = metadata?.traceability.length ?? 0;
  const numExplanationsComments = metadata?.explanations.length ?? 0;
  const enableMoreInfo =
    // more info page can show count/sku info
    timelineTooltipShowCounts ||
    // and it can show explanations / traceability
    numExplanationsComments +
      (timelineTooltipEnableSignalValidation ? numTraceabilityComments : 0) >
      0;

  const tagNameDisplay =
    pnl.category === PnlCategoriesEnum.unknown
      ? translate("No_tag")
      : tag?.name;
  const shouldDisplayTag =
    Boolean(tagNameDisplay) || tabIndex === TooltipPanel.MoreInfo;
  const modalHeight = shouldDisplayTag
    ? MODAL_HEIGHT_WITH_TAG
    : MODAL_HEIGHT_NO_TAG;
  // switch to up-pointing modal if too high
  const minModalMarginTop =
    modalHeight + MODAL_ARROW_HEIGHT + NAV_HEIGHT_PX + 40;

  const breakpointXlMultiplier = Math.max(
    1,
    innerWidth / BREAKPOINT_DESKTOP_XL
  );

  const isOutsideViewport = timeAxisTop
    ? timeAxisTop + top < minModalMarginTop * breakpointXlMultiplier
    : false;
  const showTagHighlights =
    areAnyTagsHighlighted || highlightOverlappingSegments;
  const modalTop =
    top +
    MODAL_ARROW_HEIGHT * 1.6 +
    20 +
    (isSingleTimeline ? 15 : 0) +
    // TECHDEBT: magic numbers per page?
    (isHubletPage || isGridLayoutPage ? -16 : 0) +
    (showTagHighlights && selectedTimelineIdx && selectedTimelineIdx > 0
      ? 54
      : 0);
  const pnlColor = PNL_COLORS[pnl.category];

  const offsetLeft =
    (isSingleTimeline
      ? 8
      : TIME_AXIS_MARGIN_HZ + (isMobileOrLarger ? 18 : 14)) +
    //  TECHDEBT: magic numbers per page?
    (isTimelinePage || isTrendsPage ? -38 + (!isMobileOrLarger ? 4 : 0) : 0);

  const modalOffsetTop = shouldDisplayTag ? 0 : MODAL_OFFSET_TOP_WITHOUT_TAG;
  const modalOffsetTopUpwardPointing =
    isOutsideViewport && !shouldDisplayTag ? -MODAL_OFFSET_TOP_WITHOUT_TAG : 0;

  // offset modal from the right if it overflows the screen width (mobile)
  const MODAL_OUTER_PADDING_RIGHT = 20;
  const modalRight = left + MODAL_WIDTH + MODAL_OUTER_PADDING_RIGHT;
  const doesModalOverflowRight = modalRight > innerWidth;
  const modalOffsetRight = doesModalOverflowRight ? innerWidth - modalRight : 0;

  const events = selectedProdSegment?.events ?? [];
  const retrotagEvent = events.find(event =>
    Boolean(event?.datasourceGroup?.type === "retroactive")
  );
  const retrotagOperationId = retrotagEvent?.operationId;
  return (
    <div
      className="tooltipWrapper"
      style={{
        top:
          (modalTop + (isOutsideViewport ? 0 : -modalHeight - ROW_HEIGHT * 2)) *
          breakpointXlMultiplier,
        position: "absolute",
        left: left + modalOffsetRight + offsetLeft,
        minWidth: MODAL_WIDTH,
        width: "fit-content",
        maxWidth: `calc(100% - ${offsetLeft}}px)`,
        pointerEvents: isModalPinned ? "auto" : "none",
        height: 0,
        zIndex: Z_INDEX.L1,
      }}
    >
      <TimelineTooltipStyles
        ref={ref}
        data-component-name={"TimelineTooltip"}
        className={`tooltip ${isOutsideViewport ? "upwardPointing" : ""}`}
        {...{
          $deltaX: deltaX,
          $pnlColor: pnlColor,
          $modalHeight: modalHeight,
          $modalOffsetRight: modalOffsetRight,
          $modalOffsetTop: modalOffsetTop,
          $modalOffsetTopUpwardPointing: modalOffsetTopUpwardPointing,
          $isModalPinned: isModalPinned,
          $removeRetrotagFormOpen: removeRetrotagFormOpen,
          $isDarkMode: isDarkMode,
        }}
      >
        <div
          className={`modal ${isOutsideViewport ? "arrowTop" : "arrowBottom"} ${
            isModalPinned ? "pinned" : ""
          }${tagNameDisplay ? " withTag" : ""}`}
        >
          {removeRetrotagFormOpen && machemetryId && retrotagOperationId && (
            <RemoveRetrotagForm
              closeForm={() => setRemoveRetrotagFormOpen(false)}
              segmentRange={
                {
                  ge: selectedProdSegment.range.ge,
                  lt: selectedProdSegment.range.lt,
                } as TimeRangeFinite
              }
              machemetryId={machemetryId}
              retrotagOperationId={retrotagOperationId}
            />
          )}
          <div className="pnlCategory textAlignCenter">
            <div className="pnlColorCircle" />
            <h5>{startCase(camelCase(translate(pnl.category)))}</h5>
          </div>
          {tabIndex === TooltipPanel.About && (
            <AboutPanel
              openRemoveRetrotagForm={openRemoveRetrotagForm}
              {...{
                tagNameDisplay,
                machemetryId,
              }}
            />
          )}
          {tabIndex === TooltipPanel.MoreInfo && (
            <MoreInfoPanel
              explanations={metadata?.explanations}
              traceability={
                timelineTooltipEnableSignalValidation
                  ? metadata?.traceability
                  : undefined
              }
              {...{ selectedProdSegment }}
            />
          )}
          <div className="tabs">
            <TooltipTabs
              tabIndex={tabIndex ?? 0}
              setTabIndex={setTabIndex}
              disableMoreInfo={!enableMoreInfo}
            />
          </div>
          {isModalPinned && <ButtonCloseModal />}
        </div>
      </TimelineTooltipStyles>
    </div>
  );
}

function useCloseTooltipOnMouseout() {
  const [{ isModalPinned }] = useTimelineTooltipStore();
  const ref = useRef<HTMLDivElement>(null);
  const handleCloseModal = useHandleCloseModal();
  useEffect(() => {
    if (isModalPinned) {
      return;
    }
    const handleMouseOut = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        handleCloseModal();
      }
    };
    document.addEventListener("mouseout", handleMouseOut);
    return () => document.removeEventListener("mouseout", handleMouseOut);
  }, [handleCloseModal, isModalPinned]);
  return ref;
}

/** when we scroll too far to the left/right, close the timeline tooltip */
function useCloseTooltipWhenScrollTooFar() {
  const [{ modal }] = useTimelineTooltipStore();
  const { scaleTime, xLeft, xRight } = useTimeAxisContext();
  const handleCloseModal = useHandleCloseModal();

  useEffect(() => {
    if (!modal) {
      return;
    }
    const xIdeal = (scaleTime(modal.t) || 0) - 0.5 * MODAL_WIDTH;
    if (
      xIdeal < xLeft - 0.5 * MODAL_WIDTH ||
      xIdeal > xRight - 0.5 * MODAL_WIDTH
    ) {
      handleCloseModal();
    }
  }, [xLeft, xRight, modal, scaleTime, handleCloseModal]);

  return null;
}

function ButtonCloseModal() {
  const springExpandIn = useSpring({
    from: { transform: "scale(0)" },
    to: { transform: "scale(1)" },
    config: { tension: 1000 },
    immediate: getIsCypressRunning(),
  });
  const handleCloseModal = useHandleCloseModal();
  useKey("Escape", handleCloseModal);
  return (
    <animated.div className="btnClose" style={springExpandIn}>
      <IconButton onClick={handleCloseModal} aria-label="Close" size="large">
        <CloseIcon />
      </IconButton>
    </animated.div>
  );
}
